import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../base/hooks';

import { Button } from '../../../../../base/components/Button';
import { __t } from '../../../../../base/utils/t';
import { VacancyTableFilter } from '../../enums';
import { SearchBar } from './SearchBar';
import { NavBar } from './NavBar';
import { SortingColumn } from './SortingColumn';
import { VacancyRow } from './VacancyRow';
import { vacancyListActions } from '../../redux/slices/vacancyListSlice';
import { Pagination } from '../../../../../base/components/Pagination';
import { UpgradeModal } from '../UpgradeModal';
import { VacancyLimitModal } from '../VacancyLimitModal';

import './styles.scss';
import { Icon } from '../../../../../base/components/Icon';
import { SortDirection } from '../../../../../base/enums';

export interface Props {
    filter: VacancyTableFilter;
}

const defaultSort = {
    column: 'updatedAt',
    direction: SortDirection.Desc,
};

export const VacancyTable = ({ filter }: Props) => {
    const dispatch = useAppDispatch();
    const { vacancies, currentPage, totalCount, shouldReloadLastFilter, showVacancyLimitModal } = useAppSelector((state) => state.vacancyList);
    const { currency } = useAppSelector((state) => state.employerApp.config);
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState(defaultSort);
    const [upgradeVacancyId, setUpgradeVacancyId] = useState('');
    const [showUpgradeVacancyModal, setShowUpgradeVacancyModal] = useState(false);

    useEffect(() => {
        if (shouldReloadLastFilter) {
            dispatch(
                vacancyListActions.fetchVacancyList({
                    status: filter,
                    search,
                    sort,
                    page: currentPage,
                }),
            );
            dispatch(vacancyListActions.setShouldReloadLastFilter(false));
        }
    }, [shouldReloadLastFilter, filter, search, sort, currentPage]);

    useEffect(() => {
        setSearch('');
        setSort(defaultSort);
        dispatch(vacancyListActions.fetchVacancyList({ status: filter, sort: defaultSort, page: 1 }));
    }, [filter]);

    const searchChangeHandler = (value: string) => setSearch(value);

    const clearSearch = () => {
        setSearch('');
        setSort(defaultSort);
        dispatch(
            vacancyListActions.fetchVacancyList({
                status: filter,
                sort: defaultSort,
                page: 1,
            }),
        );
    };

    const submitSearch = () => {
        setSort(defaultSort);
        dispatch(
            vacancyListActions.fetchVacancyList({
                status: filter,
                search,
                sort: defaultSort,
                page: 1,
            }),
        );
    };

    const sortHandler = (column: string, direction: SortDirection) => {
        setSort({ column, direction });
        dispatch(
            vacancyListActions.fetchVacancyList({
                status: filter,
                search,
                sort: { column, direction },
                page: 1,
            }),
        );
    };

    const paginationHandler = (page: number) => {
        dispatch(
            vacancyListActions.fetchVacancyList({
                status: filter,
                search,
                sort,
                page,
            }),
        );
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    };

    const upgradeVacancyHandler = (vacancyId: string) => {
        setUpgradeVacancyId(vacancyId);
        setShowUpgradeVacancyModal(true);
    };

    const hideUpgeradeVacancyModal = () => {
        setUpgradeVacancyId('');
        setShowUpgradeVacancyModal(false);
    };

    const tableData = useMemo(() => {
        if (!vacancies.length && search) {
            return (
                <div className='vacancy-table-component__search-nothing-found'>
                    <Icon
                        icon='magnifying-glass-thin'
                        size='80'
                        color='info'
                        className='vacancy-table-component__search-nothing-found-icon'
                    />
                    <p className='vacancy-table-component__search-nothing-found-title'>{__t('Ничего не найдено')}</p>
                    <p className='vacancy-table-component__search-nothing-found-text'>
                        {__t('Попробуйте снова поиск отредактировав название')}
                    </p>
                    <div className='vacancy-table-component__search-nothing-found-clear-link' onClick={clearSearch}>
                        {__t('Очистить параметры фильтра')}
                    </div>
                </div>
            );
        }

        if (!vacancies.length) {
            return <div className='vacancy-table-component__empty'>{__t('Нет вакансий')}</div>;
        }

        return vacancies.map((vacancy) => (
            <VacancyRow
                key={vacancy.id}
                vacancyData={vacancy}
                currencies={currency}
                upgradeVacancyHandler={upgradeVacancyHandler}
            />
        ));
    }, [vacancies]);

    return (
        <div className='vacancy-table-component'>
            <div className='razem-app__wrapper'>
                <NavBar />
                <div className='vacancy-table-component__searchbar-wrap'>
                    <SearchBar
                        value={search}
                        changeHandler={searchChangeHandler}
                        submitHandler={submitSearch}
                        clearSearch={clearSearch}
                    />
                    <Button className='vacancy-table-component__add-vacancy-button' routerLinkPath='/vacancy/create'>
                        {__t('Добавить вакансию')}
                    </Button>
                </div>

                <div className='vacancy-table'>
                    <div className='vacancy-table__header'>
                        <div className='vacancy-table__header-column vacancy-table__id-column'>
                            <SortingColumn
                                id='id'
                                title={__t('ID')}
                                sortDirection={sort.column === 'id' ? sort.direction : null}
                                onSortHandler={sortHandler}
                            />
                        </div>
                        <div className='vacancy-table__header-column vacancy-table__date-column'>
                            <SortingColumn
                                id='updatedAt'
                                title={__t('Публикация')}
                                sortDirection={sort.column === 'updatedAt' ? sort.direction : null}
                                onSortHandler={sortHandler}
                            />
                        </div>
                        <div className='vacancy-table__header-column vacancy-table__purpose-column'></div>
                        <div className='vacancy-table__header-column vacancy-table__vacancy-column'>
                            {__t('Вакансия')}
                        </div>
                        <div className='vacancy-table__header-column vacancy-table__responses-column'>
                            {__t('Отклики ')}
                        </div>
                        <div className='vacancy-table__header-column vacancy-table__cv-column'>{__t('CV')}</div>
                        <div className='vacancy-table__header-column vacancy-table__views-column'>
                            <SortingColumn
                                id='views'
                                title={__t('Просмотры')}
                                sortDirection={sort.column === 'views' ? sort.direction : null}
                                onSortHandler={sortHandler}
                            />
                        </div>
                        <div className='vacancy-table__header-column vacancy-table__actions-column'></div>
                    </div>

                    <div className='vacancy-table__content'>{tableData}</div>

                    <Pagination currentPage={currentPage} totalCount={totalCount} paginationHandler={paginationHandler} />
                </div>
            </div>

            <UpgradeModal
                vacancyId={upgradeVacancyId}
                visible={showUpgradeVacancyModal}
                closeModalHandler={hideUpgeradeVacancyModal}
            />
            <VacancyLimitModal
                visible={showVacancyLimitModal}
                visibilityHandler={() => dispatch(vacancyListActions.setShowVacancyLimitModal(false))}
            />
        </div>
    );
};
